import { FormikProps } from "formik";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { applyApiErrors } from "@/components/Formik";
import { errorEntryType, hasCode } from "@/core/api";
import { ICompanyDto, requestToJoin, serializeCompanyDto } from "@/data/companies";
import { refresh } from "@/data/session";
import { MessagesContext } from "@/eworkitComponents";
import { SessionContext } from "@/infrastructure/Session";

import { FORM_MESSANGER_CONTEXT, ICreateCompanyFormValues } from "../Create/CreateCompanyForm";

export type TAskToJoinSubmitResult = "success" | "error" | "profile-in-company";

export const useAskToJoinSubmit = () => {
  const { addMessage } = useContext(MessagesContext);
  const { update } = useContext(SessionContext);

  const { t } = useTranslation(["session", "common"]);

  return useCallback(
    async (values: ICreateCompanyFormValues, form: FormikProps<ICreateCompanyFormValues>) => {
      let status: TAskToJoinSubmitResult = "success";
      let companyId = 0;

      try {
        const newCompanyDto = serializeCompanyDto(values as ICompanyDto);

        form.setSubmitting(true);
        const response = await requestToJoin({}, newCompanyDto);

        companyId = response.company.id;

        const sessionUser = await refresh();
        update(sessionUser);
      } catch (response) {
        const payload = errorEntryType(response.payload);
        const profileInCompany = hasCode(payload, "PROFILE_IN_COMPANY");

        if (profileInCompany) {
          status = "profile-in-company";
        } else {
          addMessage({
            title: t("common:VALIDATION_MESSAGES.GENERAL"),
            type: "warning",
            context: FORM_MESSANGER_CONTEXT,
          });
          applyApiErrors(form, response.payload);

          status = "error";
        }
      } finally {
        form.setSubmitting(false);
      }

      return { status, companyId };
    },
    [addMessage, update],
  );
};
